/**
 * Empirical open-hours priors.
 *
 * Roughly 40% of Tel Aviv's OSM venues have no `opening_hours` tag. Treating those as
 * always open floods the map with phantom 04:00 activity; treating them as always
 * closed throws away a third of the city. Both are bad, and both are guesses dressed up
 * as facts.
 *
 * Instead, learn the answer from the data already present: of the sushi places that
 * *do* declare hours, what fraction are open on a Tuesday at 13:00? Apply that
 * probability to the sushi places that do not. A venue with unknown hours then
 * contributes its expected value rather than a coin flip.
 *
 * This is self-calibrating — it gets better as OSM coverage improves, and it adapts
 * automatically to the local pattern rather than to an assumption baked in here.
 */

import {
  decodeMask,
  getSlot,
  SLOTS_PER_DAY,
  TOTAL_SLOTS,
  type Venue,
  type VenueCategory,
} from '@tlv/shared';

/**
 * Smoothing strength, in pseudo-observations. A category with only a handful of
 * hours-tagged venues is pulled toward the city-wide pattern rather than trusted; a
 * category with hundreds barely moves.
 */
const SMOOTHING_K = 8;

export type OpenPriors = Map<VenueCategory, Float64Array>;

export interface PriorsResult {
  priors: OpenPriors;
  /** City-wide open rate per slot, used as the shrinkage target and the last resort. */
  global: Float64Array;
  /** How many venues had usable hours, per category. Surfaced for diagnostics. */
  sampleSizes: Map<VenueCategory, number>;
}

export function buildOpenPriors(venues: readonly Venue[]): PriorsResult {
  const openCounts = new Map<VenueCategory, Float64Array>();
  const totals = new Map<VenueCategory, number>();

  const globalOpen = new Float64Array(TOTAL_SLOTS);
  let globalTotal = 0;

  for (const venue of venues) {
    if (venue.hours === null) continue;

    const mask = decodeMask(venue.hours);

    let counts = openCounts.get(venue.category);
    if (!counts) {
      counts = new Float64Array(TOTAL_SLOTS);
      openCounts.set(venue.category, counts);
    }

    for (let day = 0; day < 7; day++) {
      for (let slot = 0; slot < SLOTS_PER_DAY; slot++) {
        if (!getSlot(mask, day, slot)) continue;
        const idx = day * SLOTS_PER_DAY + slot;
        counts[idx]!++;
        globalOpen[idx]!++;
      }
    }

    totals.set(venue.category, (totals.get(venue.category) ?? 0) + 1);
    globalTotal++;
  }

  const global = new Float64Array(TOTAL_SLOTS);
  if (globalTotal > 0) {
    for (let i = 0; i < TOTAL_SLOTS; i++) global[i] = globalOpen[i]! / globalTotal;
  }

  const priors: OpenPriors = new Map();
  for (const [category, counts] of openCounts) {
    const n = totals.get(category) ?? 0;
    const smoothed = new Float64Array(TOTAL_SLOTS);
    for (let i = 0; i < TOTAL_SLOTS; i++) {
      smoothed[i] = (counts[i]! + SMOOTHING_K * global[i]!) / (n + SMOOTHING_K);
    }
    priors.set(category, smoothed);
  }

  return { priors, global, sampleSizes: totals };
}

/**
 * Probability that a venue of this category is open at this slot.
 * Falls back to the city-wide rate for categories with no hours-tagged members at all.
 */
export function openProbability(
  result: PriorsResult,
  category: VenueCategory,
  day: number,
  slot: number,
): number {
  const idx = day * SLOTS_PER_DAY + slot;
  const categoryPrior = result.priors.get(category);
  if (categoryPrior) return categoryPrior[idx] ?? 0;
  return result.global[idx] ?? 0;
}

/**
 * Rating counts arrive only from the optional Google enrichment layer, so most of the
 * time they are absent. When absent, every venue is treated as typical — the throughput
 * signal simply does not participate, rather than penalising venues for missing data.
 *
 * The log damps the difference: a venue with 4,000 reviews is a busier kitchen than one
 * with 40, but not a hundred times busier.
 */
export function buildRatingScale(venues: readonly Venue[]): (count: number | null) => number {
  const known = venues
    .map((v) => v.ratingCount)
    .filter((c): c is number => c !== null && c > 0)
    .sort((a, b) => a - b);

  if (known.length === 0) return () => 1;

  const median = known[Math.floor(known.length / 2)]!;
  const reference = Math.log(1 + median);

  return (count: number | null) => {
    if (count === null || count <= 0) return 1;
    return Math.log(1 + count) / reference;
  };
}
